import { Node, mergeAttributes, wrappingInputRule } from '@tiptap/core';
import { AnswerSpaceType } from '../node-answer-space';
import { Plugin } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

export interface AnswerSectionOptions {
  HTMLAttributes: Record<string, any>;
}
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    answerSection: {
      /**
       * Set a answerSection node
       */
      setAnswerSection: (spaceType: AnswerSpaceType) => ReturnType;
      /**
       * Toggle a answerSection node
       */
      toggleAnswerSection: (spaceType: AnswerSpaceType) => ReturnType;
      /**
       * Unset a answerSection node
       */
      unsetAnswerSection: () => ReturnType;
    };
  }
}

export const inputRegex = /^\s*>\s$/;

const answerSectionType = 'answerSection';

const AnswerSection = Node.create<AnswerSectionOptions>({
  name: answerSectionType,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  content: 'block+',

  group: 'block',

  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
      },
      style: {
        default: null,
      },
      spaceType: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'answer-section' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'answer-section',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {}),
      0,
    ];
  },

  addCommands() {
    return {
      setAnswerSection: (spaceType: AnswerSpaceType) => ({ commands }) => {
        return commands.wrapIn(this.name, { spaceType });
      },
      toggleAnswerSection: (spaceType: AnswerSpaceType) => ({ commands }) => {
        return commands.toggleWrap(this.name, { spaceType });
      },
      unsetAnswerSection: () => ({ commands }) => {
        return commands.lift(this.name);
      },
    };
  },

  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type,
      }),
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          decorations: ({ doc }) => {
            const decorations: Decoration[] = [];

            doc.descendants((node, pos) => {
              if (node.type.name === 'answerSection') {
                const firstChild = node.firstChild;
                const isEmpty = !node.firstChild?.content.size;

                if (isEmpty && firstChild) {
                  const decoration = Decoration.node(pos, pos + node.nodeSize, {
                    class: 'empty',
                  });
                  decorations.push(decoration);
                }
              }
              return false;
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});

export default { node: AnswerSection, type: answerSectionType };
